import { CONST } from '@/utils/constants'
import { URLS } from '@/utils/urls'
import { Discussions, Distributions, Alerts, Workspaces } from '@/api'

import navigation from '@/utils/navigation'
import store from '@/store'
import i18n from '@/i18n'
import { isNullOrUndefined } from '@/utils'
import { SessionMonitor } from '@/utils/monitors'

// Open Item with GWT
export const openWithGWT = async (to, from, next) => {
  // Get Values
  let id = to.params.id
  let type = to.meta.type
  // Skip if Required Data is missing
  if (isNullOrUndefined(id) || isNullOrUndefined(type)) return next()
  // Prepare variables
  let destination = null
  // Determine Processing Method
  switch(type) {
    // Simple Navigation/Redirection
    case "workspace":
      destination = URLS.GWT.Workspace(id)
      break
    case "user":
      destination = URLS.GWT.Profile(id)
      break
    case "group":
      destination = URLS.GWT.Groups(id)
      break
    // Resolve Destination
    case "discussion":
      try {
        // Get [GROUP] from Discussion
        const response = await Discussions.getDiscussion(id)
        const groupId =  response.data.TargetId
        // Set Destination
        destination = URLS.GWT.Groups(groupId)
      } catch (err) {
        // Redirect Home
        destination = URLS.App.Home()
      }
      break
    case "distribution":
      try {
        // Get [WORKSPACE] from Distribution
        const response = await Distributions.getDistribution(id)
        const workspaceId =  response.data.WorkspaceId
        // Set Destination
        destination = URLS.GWT.Workspace(workspaceId)
      } catch (err) {
        // Redirect Home
        destination = URLS.App.Home()
      }
      break
    case "alert":
      try {
        // Get User
        const userId = store.getters['user/userId']
        // Get [VISUALIZATION] from Alert
        const response = await Alerts.getAlert(userId, id)
        const visualizationId =  response.data.Object.Visualization.Id
        // Set Destination
        destination = URLS.App.Open.Visualization(visualizationId)
      } catch (err) {
        // Redirect Home
        destination = URLS.App.Home()
      }
      break
    // Create New Workspace to View
    case "visualization":
    case "list":
    case "chart":
    case "metric":
      try {
        // Is User Allowed to Create Workspace
        const userId = store.getters['user/userId']
        const allowed = store.getters['user/canNameWorkspace']
        if (!allowed) throw new Error('Unauthorized')
        // Create new Workspace and Add the View
        const workspaceId = await Workspaces.createTemporaryWorkspaceWithVisualization(userId, id)
        // Set Destination
        destination = URLS.GWT.Workspace(workspaceId)
      } catch (err) {
        // Redirect Home
        destination = URLS.App.Home()
      }
      break
  }
  // Redirect or Stop
  if (destination) {
    navigation.replace(destination)
  } else {
    next()
  }
}

// Force Routes to be Lowercase
export const forceLowercaseRoutes = (to, from, next) => {
  const done = Boolean(to.params._rewritten)
  const current = to.path
  const lower = current.toLowerCase()
  // Only Process Once and Only if there is an Uppercase Character used
  if (!done && current !== lower) {
    // In the Vue Router, Params (/path/:name/something) override the "path" property, so
    // if a Param contains Uppercase chars they will be injected back in and cause an
    // endless loop. To Allow Uppercase chars for Params and to prevent this loop we
    // add a "special" param to know that the URL was already "_rewritten".
    // This ensures a route like "/start/:value/end" that is matched by a request for
    // "/start/ABC/ENd" is changed to "/start/ABC/end" effectively rewritting the parts
    // of the Path that aren't Params.
    navigation.replace({ ...to, path: lower, params: { ...to.params, _rewritten: true } })
  } else {
    next()
  }
}

// Set UI Language
export const setLanguage = (to, from, next) => {
  i18n.setLanguage()
  next()
}

// Set Page Title
export const setPageTitle = (to, from, next) => {
  // Find nearest Title to current Page
  const firstTitle = to.matched.slice().reverse().find(route => route.meta && route.meta.title)
  if (firstTitle) document.title = firstTitle.meta.title
  next()
}

// Check Authorization Status
export const checkAuthorization = async (to, from, next) => {
  // Get Page Status
  const isPrivate = to.matched.some(route => route.meta.requiresAuth)
  // Allow access to Public Pages
  if (!isPrivate) {
    // Stop Monitoring
    SessionMonitor.stop()
    return next()
  }
  // Get Current User Info
  try {
    // Fetch User
    await store.dispatch('user/retrieveCurrentUser')
    // Confirm Valid UserId
    const userId = store.getters['user/userId']
    if (!userId) throw new Error('Unauthorized')
    // Start Monitoring
    SessionMonitor.start()
    // Allow Access
    next()
  } catch (err) {
    // Stop Monitoring
    SessionMonitor.stop()
    // Unauthorized = Redirect with AbsURL Continuation Query Param Attached
    navigation.push({ name: 'Login', query: { continue: `${CONST.Services.Paths.Vue}${to.path}` }, hash: to.hash })
  }
}

export const middleware = {
  openWithGWT,
  forceLowercaseRoutes,
  setLanguage,
  setPageTitle,
  checkAuthorization
}

export default middleware
